import React, { useState, useMemo, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Get_All_Top_Saller_By_MonthYear_Action } from '../../Redux/Actions/SalesPereformanceAction';
import CircularProgress from '@mui/material/CircularProgress';
import { useTheme } from '@mui/material';
import SystemUpdateAltOutlinedIcon from '@mui/icons-material/SystemUpdateAltOutlined';
import SsidChartIcon from '@mui/icons-material/SsidChart';
import SearchIcon from '@mui/icons-material/Search';
import * as XLSX from 'xlsx';

const EXCLUDE = ['Default','No Sales','Default M','Default T','Default S','Deveolper','Dev'];
const accent  = '#0D47A1';
const gold    = '#B8860B';

const formatAmt = (v) => {
  if (!v) return '$0';
  if (v >= 1e6) return `$${(v / 1e6).toFixed(1)}M`;
  if (v >= 1e3) return `$${(v / 1e3).toFixed(0)}K`;
  return `$${v}`;
};

/* ══════════════════════════════════════════════════════════════════ */
const MonthlySalesStats = () => {
  const theme   = useTheme();
  const dark    = theme.palette.mode === 'dark';
  const dispatch  = useDispatch();
  const navigate  = useNavigate();

  const [selectedMonth,     setSelectedMonth]     = useState(new Date().getMonth());
  const [selectedYear,      setSelectedYear]      = useState(new Date().getFullYear());
  const [selectedFilterType,setSelectedFilterType]= useState('Leads');
  const [searchQuery,       setSearchQuery]       = useState('');
  const [loading,           setLoading]           = useState(false);

  /* tokens */
  const border   = dark ? 'rgba(255,255,255,0.07)' : '#E8EAF0';
  const textPri  = dark ? '#E2E8F0' : '#111827';
  const textSec  = dark ? '#94A3B8' : '#6B7280';
  const cardBg   = dark ? '#1E1E2E' : '#FFFFFF';
  const inputBg  = dark ? '#2A2A3E' : '#FFFFFF';
  const scrollBg = dark ? '#16161F' : '#F0F2F8';

  const months = useMemo(() =>
    Array.from({ length: 12 }, (_, i) =>
      new Date(0, i).toLocaleString('default', { month: 'long' })
    ), []);

  const years = useMemo(() => {
    const cur = new Date().getFullYear();
    return Array.from({ length: cur - 1999 }, (_, i) => 2000 + i).reverse();
  }, []);

  useEffect(() => {
    const getData = async () => {
      setLoading(true);
      await dispatch(Get_All_Top_Saller_By_MonthYear_Action(selectedYear, selectedMonth + 1));
      setLoading(false);
    };
    getData();
  }, [selectedMonth, selectedYear, dispatch]);

  const ressalesdata = useSelector((s) => s?.AllSalesPerformance?.salesperformansmonthandyear) || {};
  const salesData = (Array.isArray(ressalesdata?.data) ? ressalesdata.data : [])
    .filter((s) => !EXCLUDE.includes(s.salesName));

  const formatMonthKey = (y, mIdx) => `${y}-${String(mIdx + 1).padStart(2, '0')}`;

  const sortedSalesData = [...salesData].sort((a, b) => {
    const key  = formatMonthKey(selectedYear, selectedMonth);
    const aL   = a.leadsPerMonth?.[key] || 0;
    const bL   = b.leadsPerMonth?.[key] || 0;
    const aD   = a.dealsPerMonth?.[key] || 0;
    const bD   = b.dealsPerMonth?.[key] || 0;
    const aSR  = aL > 0 ? (aD / aL) * 100 : 0;
    const bSR  = bL > 0 ? (bD / bL) * 100 : 0;
    switch (selectedFilterType) {
      case 'Deals':        return bD  - aD;
      case 'Leads':        return bL  - aL;
      case 'Success Rate': return bSR - aSR;
      case 'Amount':       return (b.amount||0) - (a.amount||0);
      default:             return 0;
    }
  });

  /* ── filter by search ── */
  const filteredSalesData = sortedSalesData.filter((s) =>
    s.salesName.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const exportToExcel = () => {
    const key = formatMonthKey(selectedYear, selectedMonth);
    const rows = salesData.map((s) => {
      const leads = s.leadsPerMonth?.[key] || 0;
      const deals = s.dealsPerMonth?.[key] || 0;
      return {
        'Sales Name'   : s.salesName,
        'Leads'        : leads,
        'Deals'        : deals,
        'Success Rate' : `${((deals / (leads || 1)) * 100).toFixed(1)}%`,
        'Amount'       : s.amount || 0,
      };
    });
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Sales Performance');
    XLSX.writeFile(wb, `sales-performance-${selectedYear}-${selectedMonth + 1}.xlsx`);
  };

  const selStyle = {
    padding    : '6px 12px',
    borderRadius: 8,
    border     : `1px solid ${border}`,
    background : inputBg,
    color      : textPri,
    fontWeight : 600,
    fontSize   : 13,
    cursor     : 'pointer',
    outline    : 'none',
  };

  /* ── render ── */
  return (
    <div style={{ padding: '28px 0', display: 'flex', flexDirection: 'column', height: '100%' }}>

      {/* ── HEADER ROW ── */}
      <div style={{ display:'flex', flexWrap:'wrap', alignItems:'center', gap:12, marginBottom:20 }}>

        {/* title */}
        <div style={{ marginRight:16 }}>
          <div style={{ fontWeight:700, fontSize:20, color:textPri, fontFamily:"'DM Sans',sans-serif" }}>
            Sales Performance Analysis
          </div>
          <div style={{ fontSize:12, color:textSec, marginTop:2 }}>
            Detailed agent-level metrics and historical trends
          </div>
        </div>

        {/* month pills */}
        <div style={{ display:'flex', overflowX:'auto', gap:6, flex:1, scrollbarWidth:'none', paddingBottom:2 }}>
          {months.map((month, index) => (
            <button
              key={index}
              onClick={() => setSelectedMonth(index)}
              style={{
                padding    : '6px 14px',
                borderRadius: 20,
                border     : 'none',
                cursor     : 'pointer',
                whiteSpace : 'nowrap',
                fontSize   : 13,
                fontWeight : selectedMonth === index ? 700 : 500,
                background : selectedMonth === index ? accent : 'transparent',
                color      : selectedMonth === index ? '#fff' : textSec,
                transition : 'all 0.15s',
                flexShrink : 0,
              }}
            >
              {month}
            </button>
          ))}
        </div>

        {/* year select */}
        <select value={selectedYear} onChange={(e) => setSelectedYear(Number(e.target.value))} style={selStyle}>
          {years.map((y) => <option key={y} value={y}>{y}</option>)}
        </select>

        {/* filter select */}
        <select value={selectedFilterType} onChange={(e) => setSelectedFilterType(e.target.value)} style={selStyle}>
          {['Leads','Deals','Success Rate','Amount'].map((f) => <option key={f} value={f}>{f}</option>)}
        </select>
      </div>

      {/* ── TOOLBAR ROW ── */}
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16, flexWrap:'wrap', gap:12 }}>

        {/* export excel */}
        <button
          onClick={exportToExcel}
          style={{
            display    : 'flex',
            alignItems : 'center',
            gap        : 7,
            padding    : '9px 18px',
            background : accent,
            color      : '#fff',
            border     : 'none',
            borderRadius: 10,
            cursor     : 'pointer',
            fontSize   : 13,
            fontWeight : 700,
            fontFamily : "'DM Sans',sans-serif",
          }}
        >
          <SystemUpdateAltOutlinedIcon style={{ fontSize:16 }} />
          Export to Excel
        </button>

        {/* search input */}
        <div style={{ position:'relative', display:'flex', alignItems:'center' }}>
          <SearchIcon
            style={{
              position : 'absolute',
              left     : 10,
              fontSize : 18,
              color    : textSec,
              pointerEvents: 'none',
            }}
          />
          <input
            type="text"
            placeholder="Search sales name..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={{
              paddingLeft  : 34,
              paddingRight : 12,
              paddingTop   : 8,
              paddingBottom: 8,
              borderRadius : 10,
              border       : `1px solid ${border}`,
              background   : inputBg,
              color        : textPri,
              fontSize     : 13,
              fontWeight   : 500,
              outline      : 'none',
              width        : 220,
              fontFamily   : "'DM Sans',sans-serif",
              transition   : 'border 0.15s',
            }}
            onFocus={(e)  => { e.target.style.border = `1px solid ${accent}`; }}
            onBlur={(e)   => { e.target.style.border = `1px solid ${border}`; }}
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              style={{
                position   : 'absolute',
                right      : 8,
                background : 'none',
                border     : 'none',
                cursor     : 'pointer',
                color      : textSec,
                fontSize   : 16,
                lineHeight : 1,
                padding    : 0,
              }}
            >
              ×
            </button>
          )}
        </div>

        {/* legend */}
        <div style={{ display:'flex', alignItems:'center', gap:16 }}>
          {[['Leads', accent],['Done Deals Per Sales', gold]].map(([label, col]) => (
            <span key={label} style={{ display:'flex', alignItems:'center', gap:6, fontSize:12, fontWeight:600, color:textSec }}>
              <span style={{ width:10, height:10, borderRadius:'50%', background:col, display:'inline-block' }} />
              {label.toUpperCase()}
            </span>
          ))}
        </div>
      </div>

      {/* ── SCROLLABLE CONTAINER ── */}
      <div
        style={{
          flex        : 1,
          overflowY   : 'auto',
          background  : scrollBg,
          borderRadius: 16,
          border      : `1px solid ${border}`,
          padding     : 20,
          minHeight   : 0,
          maxHeight   : '65vh',
          /* custom scrollbar */
          scrollbarWidth    : 'thin',
          scrollbarColor    : `${accent}55 transparent`,
        }}
      >
        {loading ? (
          <div style={{ display:'flex', justifyContent:'center', padding:'60px 0' }}>
            <CircularProgress style={{ color: accent }} />
          </div>
        ) : filteredSalesData.length === 0 ? (
          <div style={{ textAlign:'center', padding:'60px 0', color: textSec, fontSize:14 }}>
            {searchQuery ? `No results for "${searchQuery}"` : 'No sales data for this period'}
          </div>
        ) : (
          <>
            {/* result count */}
            <div style={{ fontSize:12, color:textSec, marginBottom:16, fontWeight:600 }}>
              Showing {filteredSalesData.length} of {sortedSalesData.length} agents
              {searchQuery && <span style={{ color:accent }}> · filtered by "{searchQuery}"</span>}
            </div>

            <div style={{
              display             : 'grid',
              gridTemplateColumns : 'repeat(4, 1fr)',
              gap                 : 16,
            }}>
              {filteredSalesData.map((sales) => {
                const key         = formatMonthKey(selectedYear, selectedMonth);
                const leads       = sales.leadsPerMonth?.[key] || 0;
                const deals       = sales.dealsPerMonth?.[key] || 0;
                const successRate = leads > 0 ? ((deals / leads) * 100).toFixed(1) : '0.0';

                return (
                  <div
                    key={sales.salesId}
                    style={{
                      background   : cardBg,
                      border       : `1px solid ${border}`,
                      borderRadius : 16,
                      padding      : '20px',
                      display      : 'flex',
                      flexDirection: 'column',
                      gap          : 0,
                      transition   : 'box-shadow 0.18s',
                      width        : '100%',
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.boxShadow = '0 4px 20px rgba(0,0,0,0.08)'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.boxShadow = 'none'; }}
                  >
                    {/* card header */}
                    <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16 }}>
                      <span style={{ fontWeight:700, fontSize:15, color:textPri, fontFamily:"'DM Sans',sans-serif" }}>
                        {sales.salesName}
                      </span>
                    </div>

                    {/* stats */}
                    <div style={{ display:'flex', flexDirection:'column', gap:8, marginBottom:16 }}>
                      {[
                        {
                          icon: (
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={textSec} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/>
                              <path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>
                            </svg>
                          ),
                          label: 'Leads', value: leads,
                        },
                        {
                          icon: (
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={textSec} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                              <polyline points="20 6 9 17 4 12"/>
                            </svg>
                          ),
                          label: 'Deals', value: deals,
                        },
                        {
                          icon: (
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={textSec} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M3 17L9 11L13 15L21 7"/><path d="M21 7H15"/>
                            </svg>
                          ),
                          label: 'Success Rate', value: `${successRate}%`,
                        },
                        {
                          icon: (
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={textSec} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                              <circle cx="12" cy="12" r="10"/><path d="M12 6v12"/>
                              <path d="M15 9h-6a3 3 0 0 0 0 6h6a3 3 0 0 0 0-6z"/>
                            </svg>
                          ),
                          label: 'Amount', value: formatAmt(sales.amount),
                        },
                      ].map(({ icon, label, value }) => (
                        <div key={label} style={{ display:'flex', alignItems:'center', justifyContent:'space-between', fontSize:13, color:textSec }}>
                          <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                            {icon}
                            <span>{label}:</span>
                          </div>
                          <strong style={{ color:textPri }}>{value}</strong>
                        </div>
                      ))}
                    </div>

                    {/* View Full Report button */}
                    <button
                      onClick={() => navigate('/SalesReportCountLeadsInMonths')}
                      style={{
                        marginTop   : 14,
                        width       : '100%',
                        padding     : '10px 0',
                        background  : accent,
                        color       : '#fff',
                        border      : 'none',
                        borderRadius: 10,
                        cursor      : 'pointer',
                        fontSize    : 13,
                        fontWeight  : 700,
                        display     : 'flex',
                        alignItems  : 'center',
                        justifyContent: 'center',
                        gap         : 7,
                        fontFamily  : "'DM Sans',sans-serif",
                        transition  : 'background 0.15s',
                      }}
                      onMouseEnter={(e) => { e.currentTarget.style.background = '#0D47A1'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.background = accent; }}
                    >
                      View Full Report
                      <SsidChartIcon style={{ fontSize:16 }} />
                    </button>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default MonthlySalesStats;
